/**
 * THE USER GUIDE CONTENT STORE — the one place guide text lives.
 *
 * Written at Block 5 closure from the modules as BUILT, not from any spec.
 * The previous guide (in ModulePages.jsx) described an app that never
 * shipped: an "Office Manager" role, "Casual, Sick, Earned" leave types,
 * "Mark Present"/"End Day" buttons, CSV exports and training videos — none
 * of which exist. Its replacement follows three rules:
 *
 *   1. Every block is tagged with the ROLE KEYS it applies to, and the page
 *      shows a reader only their own blocks. Role keys are the ones the
 *      backend uses; display names come from ROLE_LABELS below.
 *   2. Button and field names are quoted VERBATIM from the source, and each
 *      block's `ui_refs` lists the data-testids of the controls it names.
 *      `userGuide.test.js` fails on any ref that no component carries — so
 *      a renamed button breaks the guide's build, not the reader's trust.
 *   3. The guide never describes a value no record holds. Where the data is
 *      not yet entered (prices, PL opening balances), the guide describes
 *      the "Not set" / "not computable" presentation the app actually shows.
 *
 * Coverage is tested: every Sidebar nav item a role can see has at least one
 * block for that role, and no section names a route the Sidebar does not
 * serve. Update this file when a screen changes; the tests say where.
 */

export const GUIDE_VERSION = "2026-09-08";

export const ROLE_LABELS = {
  sales_officer: "Sales Officer",
  assistant_manager: "Sales Manager",
  admin_manager: "Admin Manager",
  admin: "Tech Admin",
  director: "Director",
};

const ALL = ["sales_officer", "assistant_manager", "admin_manager", "admin", "director"];
const APPROVERS = ["assistant_manager", "admin_manager", "admin", "director"];
const MASTER_ADMINS = ["admin_manager", "admin", "director"];
const COMPANY_VIEW = ["admin", "director"];

export const GUIDE_SECTIONS = [
  {
    nav: "/dashboard",
    title: "Dashboard",
    blocks: [
      {
        id: "dashboard-overview",
        roles: ALL,
        heading: "Your home screen",
        body:
          "The Dashboard is the first screen after signing in. Every tile is a door: click it to open the module it summarises. What the tiles show depends on your role — your own day for a Sales Officer, your team for a Sales Manager, the whole company for the Admin Manager, Tech Admin and Director.",
        ui_refs: [],
      },
      {
        id: "dashboard-so",
        roles: ["sales_officer"],
        heading: "The Sales Officer view",
        body:
          "Three quick actions sit at the top: \"Add Visit\" (log a doctor or chemist visit), \"Apply Leave\" (casual or privilege leave — sickness files under CL) and \"Submit Expense\". Below them, tiles show Today's Plan (doctors scheduled), Today Completed, This Month's visits, your Leave Balance as \"CL n · PL n\", and Pending Expenses — which reads \"All clear\" when nothing is waiting and \"Not loaded\" when the figure could not be fetched, never a silent zero. \"My upcoming tour\" previews your next 3 planned days; if you have no plan yet it says so and offers \"Open Route Planner\".",
        ui_refs: [
          "quick-action-add-visit",
          "quick-action-apply-leave",
          "quick-action-submit-expense",
          "stat-today-planned",
          "stat-leave-balance",
          "stat-pending-expenses",
          "upcoming-tour-empty",
        ],
      },
      {
        id: "dashboard-managers",
        roles: ["assistant_manager", "admin_manager"],
        heading: "The manager view",
        body:
          "Sales Managers see \"You + team\" scope; the Admin Manager sees \"Company\". Tiles cover My Doctors, team size, Visits Planned and Completed, DCR Submission, Approvals Pending (expenses, leaves and tour plans), Stock at Doctors and Distributors Unplaced — the count of distributors visible to no officer. Where a stock value cannot be computed yet (product prices not entered), the tile says \"value not computable\" and lists the gaps rather than showing a wrong number. \"My own claims\" tracks the expenses and leaves you filed that are waiting on an approver, and \"My team\" shows each person's status today — \"Visited today\", \"No visit recorded today\", \"On leave\", or \"Status not reported\".",
        ui_refs: [
          "stat-my-doctors",
          "stat-team-size",
          "stat-approvals-pending",
          "stat-stock-at-doctors",
          "stat-distributor-gap",
          "stock-gaps",
        ],
      },
      {
        id: "dashboard-director",
        roles: COMPANY_VIEW,
        heading: "The company view",
        body:
          "Tech Admin and Director land on the company-wide pulse: Field staff (Officers + Sales Managers), Managers, Visits Planned and Completed today, the DCR Submission rate (per head, per calendar day) and Approvals Pending. \"Top field staff · This month\" ranks by logged visits, and \"Recent activity\" lists the last 10 actions across the company.",
        ui_refs: ["stat-active-sos", "stat-visits-planned", "stat-dcr-rate", "top-field-staff-empty"],
      },
    ],
  },
  {
    nav: "/approvals",
    title: "Approvals",
    blocks: [
      {
        id: "approvals-queue",
        roles: APPROVERS,
        heading: "Waiting for you",
        body:
          "The Approvals page opens on everything waiting for YOUR signature — expenses, leaves, tour plans, team changes, master changes and new doctors in one queue. The sidebar badge carries the same count. Three tabs switch the view: \"Pending\" (act here), \"In flight\" (claims you signed at stage 1, now with the final approver) and \"Decided\" (everything you decided that has reached its end state, filterable by filing date). When your queue is empty the page says \"All caught up.\" — that is a real empty, not a loading failure, which would show an error instead.",
        ui_refs: [
          "approvals-heading",
          "sidebar-approvals-badge",
          "approvals-view-pending",
          "approvals-view-in_flight",
          "approvals-view-decided",
          "approvals-history-from",
          "approvals-empty",
        ],
      },
      {
        id: "approvals-stages",
        roles: APPROVERS,
        heading: "Stage 1 verifies; stage 2 approves",
        body:
          "A claim (expense, leave, tour plan) needs two signatures by different people. Each card shows \"Stage 1 of 2\" or \"Stage 2 of 2 · Final\". Your stage-1 approval VERIFIES the claim and moves it forward — nothing else happens: no money is released, no leave balance is spent. Only the final signature has effects. Rejecting at stage 2 is not the end of the claim: it returns to the stage-1 approver as rework, carrying your reason, and the card shows \"Rework ×N\" when it has been around before. Rejecting at stage 1 is terminal. Every rejection requires a reason — the dialog will not confirm without one. Tour plans use \"Send back\" instead of \"Reject\": the plan returns to the officer as an editable draft with your remark on it.",
        ui_refs: ["approval-stage-", "approval-approve-", "approval-reject-", "approval-remark"],
      },
      {
        id: "approvals-chips",
        roles: APPROVERS,
        heading: "Reading the cards",
        body:
          "Chips on a card are facts stamped at filing: \"Escalated\" (an expense above the escalation threshold, routed past the Sales Manager), \"Late filed\" (submitted outside its filing window), \"Retrospective\", and how long it has waited. \"Select all\" plus \"Approve selected\" bulk-approves; the confirmation states the total — \"Includes ₹X in expenses. This cannot be undone from here.\" A row that fails inside a bulk approval (already decided, not yours to sign) fails alone; the others still land.",
        ui_refs: ["approval-select-all", "approvals-bulk-approve", "approvals-bulk-confirm"],
      },
      {
        id: "approvals-new-doctor",
        roles: ["admin_manager", "director"],
        heading: "Verifying a doctor added from the field",
        body:
          "A doctor an officer added on an ad hoc call arrives as a \"New doctor\" card, with the officer's name, the specialisation and clinic area they typed, and a link to the visit it came with. Open the visit first. Three decisions: \"Approve\" if the doctor is real and new — they join the officer's list and every count. \"Merge\" if the doctor already exists under another entry: search the doctor master by name, pick the existing doctor, and the visit is moved to them; the new entry is retired. \"Reject\" with a reason if the company will not keep the doctor: the entry is retired and the visit is kept, still readable. Until you decide, the doctor is visible only to the officer who added them and cannot be put on a plan. Field officers' new doctors come to the Admin Manager; one the Admin Manager adds on her own call goes to the Director.",
        ui_refs: ["approval-merge-", "approval-merge-search", "approval-merge-confirm"],
      },
    ],
  },
  {
    nav: "/attendance",
    title: "Attendance",
    blocks: [
      {
        id: "attendance-checkin",
        roles: ALL,
        heading: "Checking in and out",
        body:
          "Attendance is a check-in, not a roll call. Press \"Check In\" at the start of your working day and \"Check Out\" at the end; the card shows today's status and times. On an approved leave day the app refuses the check-in and marks you \"On leave\" instead. Managers check in from the card at the top of their team page — field managers work the field too, and their own attendance counts like anyone's.",
        ui_refs: ["self-checkin-button", "self-checkout-button", "self-today-status"],
      },
      {
        id: "attendance-gap",
        roles: ALL,
        heading: "A day with no record is a gap, not an absence",
        body:
          "There is no \"Absent\" in this app. A day nobody captured shows as \"Not checked in\" or \"No record\", with the help text \"Nothing was captured for this day. This is not a record of absence.\" A missing check-in can be a dead phone, a field day that started before the app opened, or a day off — the app records what it saw and claims nothing more. The monthly summary counts each state separately: \"Present N · On leave N · Not checked in N · Holiday N\", with a check-in rate over working days.",
        ui_refs: ["my-attendance-summary", "self-attendance-calendar"],
      },
      {
        id: "attendance-team",
        roles: ["assistant_manager", "admin_manager"],
        heading: "Your team's day, and its history",
        body:
          "\"Today's team\" shows each report's card with check-in and check-out times; people with nothing captured yet are named in one banner line so you can follow up. The \"History\" section below shows any date window (defaulting to the last 7 days), grouped by day, newest first — and an empty window says exactly that: \"No attendance was captured in this window. A day with no record is a gap, not an absence.\"",
        ui_refs: ["team-attendance-history", "attendance-history-from", "attendance-alert"],
      },
      {
        id: "attendance-company",
        roles: COMPANY_VIEW,
        heading: "The company month",
        body:
          "Tech Admin and Director see \"Company Attendance\": every employee's month in one grid, with the month's working-day count in the heading and arrows to move between months. The same vocabulary applies — an uncaptured day is presented as uncaptured, never coloured as a status.",
        ui_refs: ["all-attendance-heading", "attendance-prev-month"],
      },
    ],
  },
  {
    nav: "/doctors",
    title: "Doctors",
    blocks: [
      {
        id: "doctors-master",
        roles: ALL,
        heading: "The doctor master",
        body:
          "Every doctor the company calls on, in one list. Search by name, clinic or territory; filter by category (\"A — High value\", \"B — Medium value\", \"C — Standard\"), specialization, or assignee. Sales Officers see the doctors assigned to them; managers and above browse the full master. \"Show archived doctors\" reveals retired records — archiving hides a doctor from lists and pickers but keeps the record and all visit history, and archived doctors can be restored at any time. Nothing is ever deleted.",
        ui_refs: ["doctor-search-input", "doctor-filter-category", "doctor-show-archived"],
      },
      {
        id: "doctors-edit",
        roles: APPROVERS,
        heading: "Adding and editing",
        body:
          "\"Add Doctor\" opens the form: name, clinic, specialization, category, territory unit (required — and the HQ/Office location is refused, it is not a field territory), pin code, mobile (validated as a real Indian mobile — junk numbers are refused at the door), monthly visit target, and assignment. A doctor may have a Sales Officer, a Sales Manager, or both. Saving an edit FILES a master change for approval — the record itself changes only when the final approver signs, and the toast tells you who that is.",
        ui_refs: ["doctor-add-button", "doctor-form-submit", "doctor-territory-hint"],
      },
      {
        id: "doctors-territory-tools",
        roles: MASTER_ADMINS,
        heading: "Territory repair tools",
        body:
          "Records that arrived without a usable territory show \"Territory needs review\" with the original value preserved. The bulk bar (\"N doctor(s) need a territory unit\") assigns a unit to a selected group in one action, and the suggestion panel reads clinic addresses and proposes a Patna zone per doctor — \"nothing is applied until you tick it\", and an ambiguous address gets no suggestion rather than a guess. Archiving a doctor is a Tech Admin / Director action.",
        ui_refs: ["bulk-territory-bar", "bulk-territory-apply", "territory-suggestions", "territory-suggestions-apply"],
      },
    ],
  },
  {
    nav: "/doctor-visits",
    title: "Doctor Visits",
    blocks: [
      {
        id: "visits-log",
        roles: ALL,
        heading: "Logging a visit (the DCR)",
        body:
          "Tap \"Add Visit\" on the Dashboard. The screen is \"Log a doctor visit\". This is your Daily Call Report. Pick the doctor; only your own book is offered. Pick the visit date. Today is the default, and the note under the field says how far back you may go. If that note cannot be read from the server, the date is blocked, not guessed. Tick the products discussed. Tap \"Add sample\" for each sample given. Pick the doctor's feedback; it is required. Set a follow-up date if you want one. Tap \"Submit visit\". If you came here from \"Mark visited\" on your plan, the doctor and today's date are already filled in; just complete the rest. If the call was not on your plan, turn on the \"Ad hoc call\" switch and pick a reason from the list. Without a reason the app refuses and names the field. For a planned doctor on the planned day, leave the switch off; the server counts it as planned on its own. Sales Managers log visits to their own doctors here the same way. If no doctors are assigned to you yet, the form says so: ask your manager to assign your book. A failed doctor list shows an error, never \"no doctors\". With the \"Ad hoc call\" switch on, the doctor list widens: a search box appears and the list shows your own doctors plus every verified doctor in your territory units, including doctors on a colleague's list, named as \"· <name>'s list\". You can log that visit; a note under the field says it will be marked as another officer's doctor for your manager. If the doctor is not in the list at all, tap \"Doctor not found? Add new doctor\" and enter the name, specialisation and clinic area. The visit and the new doctor are saved together, in one go. The doctor is sent to the Admin Manager for verification; until then only you can see them, and they cannot be put on a plan. If the territory list cannot be read, the form says so and shows your own list only.",
        ui_refs: [
          "visit-input-doctor",
          "visit-input-date",
          "visit-date-window",
          "visit-add-sample",
          "visit-input-feedback",
          "visit-input-adhoc-toggle",
          "visit-input-adhoc-reason",
          "visit-input-doctor-search",
          "visit-other-officer-note",
          "visit-quick-add-open",
          "visit-quick-add",
          "visit-no-doctors",
          "visit-submit-button",
        ],
      },
      {
        id: "visits-location",
        roles: ALL,
        heading: "Location and consent",
        body:
          "A visit is counted by the DCR you file, not by where your phone is. Tapping \"Mark visited\" does not read your location. The \"Location\" card at the bottom of the visit form appears only if location capture is switched on. Then it asks for your consent with a switch. The status line says what will happen: \"Your location will be read once, when you submit.\", \"Location captured for this visit.\", or \"Not captured.\" with the reason. Saying no is recorded as no. That is different from never being asked. The visit saves either way.",
        ui_refs: ["visit-location-consent", "visit-location-status"],
      },
      {
        id: "visits-review",
        roles: APPROVERS,
        heading: "Team and company views",
        body:
          "Sales Managers and the Admin Manager see \"Team Daily Call Report\": every report's visits, filterable by person and category. Tech Admin and Director see \"Company-wide DCR\" with Today / This Week / This Month counts. Open any visit for its detail: products, samples, notes, follow-up (an overdue one is flagged), and location, shown as \"Not captured.\" when it was not. A visit filed with the \"Ad hoc call\" switch on carries an \"Ad hoc\" badge and the reason the officer picked. A visit to a planned doctor on the planned day is recorded against the plan; the officer does nothing extra for that. Sales Managers file their own visits from \"Log a doctor visit\" and mark visited from their own plan, the same as officers. A visit logged on an ad hoc call to a doctor on a colleague's list carries an \"Other officer's doctor\" badge naming whose; a visit to a doctor added from the field carries \"Doctor awaiting verification\" until the Admin Manager decides. Above the filters, the \"Plan deviation\" table shows four numbers per officer for the chosen dates (month to date until you pick dates): Planned, As planned, Unplanned, Missed. Missed is a doctor on the approved plan for a day with no visit logged that day. The table says so under its heading: the app knows no visit was logged, not whether the officer went. Zeros are shown as zeros; if the table cannot be read it says that instead of showing an empty table.",
        ui_refs: ["log-visit-cta", "visit-followup-overdue", "visit-adhoc-", "visit-other-officer-", "visit-pending-doctor-", "team-plan-deviation", "all-plan-deviation"],
      },
    ],
  },
  {
    nav: "/route-planner",
    title: "Route Planner",
    blocks: [
      {
        id: "tour-plan-build",
        roles: ["sales_officer", "assistant_manager"],
        heading: "Building and submitting your monthly plan",
        body:
          "Open Route Planner. The screen is \"My Tour Plan\", a month grid. Plan the whole month, not just the first day. Tap a day. Type the \"Area / locality\". Tick the doctors you will see. Tap \"Save day\". Do this for every working day. Sundays and declared holidays cannot be planned. A day inside your approved leave cannot be planned either. When the month is ready, tap \"Submit plan\". A Sales Officer's plan goes to your Sales Manager first, then the Admin Manager. A Sales Manager's plan goes to the Admin Manager first, then the Director. If you submit fewer than five days with most of the month still ahead, the app asks \"Submit a partial month?\"; tap \"Submit anyway\" if you mean it. Sales Managers plan their own month on this same screen, the same way.",
        ui_refs: ["tour-plan-day-area", "tour-plan-doctor-", "tour-plan-day-save", "tour-plan-submit", "tour-plan-thin-confirm"],
      },
      {
        id: "tour-plan-locked",
        roles: ["sales_officer", "assistant_manager"],
        heading: "When the grid is locked, and how it opens again",
        body:
          "After \"Submit plan\" the grid is locked. You cannot add or change days. A notice above the grid names who holds the plan. Tapping the grid repeats that notice; the page is not broken. Your Sales Manager can \"Send back\" the plan. It comes back as a draft with their remark. Fix it and submit again. Once approved, the grid stays locked for the month. To change days, ask the approver named in the notice to reopen it. Locked does not mean idle. This is where you walk the day. On My Tour Plan (and, for Sales Officers, on the Dashboard too) today's planned day is a card. It lists each planned doctor with a \"Mark visited\" button. Tap it. \"Log a doctor visit\" opens with that doctor and today's date already filled in. Complete the DCR as usual (products, samples, feedback) and tap \"Submit visit\". The visit is recorded against the plan. The doctor gets a tick on the card, and the card reads \"N of M visited\". Marking visited never changes the plan itself. For Sales Officers, the Dashboard's \"Today Completed\" tile counts planned doctors visited today, with unplanned calls as a separate number beside it.",
        ui_refs: [
          "tour-plan-locked-notice",
          "tour-plan-grid",
          "planned-day-card",
          "mark-visited-",
          "planned-day-progress",
          "visit-submit-button",
          "stat-today-completed",
        ],
      },
      {
        id: "tour-plan-approve",
        roles: APPROVERS,
        heading: "Approving and sending back",
        body:
          "Team plans waiting on you appear with their days, areas and named doctors. Tap \"Approve\" to move a plan forward; the final signature locks the month for its owner. Tap \"Send back\" (plans have no \"Reject\") to return it as a draft with your reason attached. \"Reopen\" acts on a plan that is already approved. It returns the month to draft so days can be changed, and the reason you give is recorded on the plan. It is offered to the officer's own Sales Manager and to the Admin Manager, and only while the month is still running. A visit never needs a reopen: \"Mark visited\" records a DCR against the plan and leaves the plan itself untouched. Sales Managers plan and mark visited on their own plans exactly as officers do. The Director sees every plan and gives the final signature on a Sales Manager's plan. The Admin Manager oversees plans but files none.",
        ui_refs: [
          "team-tour-plan-approve-",
          "team-tour-plan-sendback-",
          "team-tour-plan-confirm",
          "team-tour-plan-remark",
          "team-tour-plan-reopen-",
          "all-tour-plan-reopen-",
        ],
      },
      {
        id: "tour-plan-vs-actual",
        roles: ALL,
        heading: "Plan vs actual",
        body:
          "On My Tour Plan, tap \"Plan vs actual →\". It opens the plan's detail page. The \"Plan vs actual\" panel compares what you planned for the month with the DCRs filed for the month. Four numbers: Planned, Visited, No visit recorded, and Unplanned visits. \"Visited\" is a planned doctor with a DCR on the planned day. \"No visit recorded\" means nobody filed a DCR. The panel says so in words: \"Nobody filed a DCR for these. That is not a record that the visit did not happen.\" This panel never says \"missed\". The team view's \"Plan deviation\" table does, and there it means exactly the same gap: no DCR filed for a planned doctor on the planned day, not that you did not go. \"Unplanned visits\" are calls filed with the \"Ad hoc call\" switch on, or to a doctor who was not on that day's plan. The source line says which copy it used: \"Compared against the plan as approved\", or the current draft when no approved copy exists. Old plans written before plans named their doctors show \"Doctors not named\". That is a gap, not a claim that no doctors were planned.",
        ui_refs: [
          "my-tour-plan-vs-actual-link",
          "plan-vs-actual",
          "plan-vs-actual-source",
          "pva-visited",
          "pva-no-visit",
          "pva-unplanned",
          "plan-vs-actual-gap",
        ],
      },
    ],
  },
  {
    nav: "/expenses",
    title: "Expenses",
    blocks: [
      {
        id: "expenses-file",
        roles: ALL,
        heading: "Filing an expense",
        body:
          "\"New expense\" takes the date, one or more line items (type and amount — \"Add another category\" adds a line), the details, an optional linked doctor from your own book, and receipts. Types come from Settings, so the list is always the one approvals accept. A type with a cap states it up front — \"Capped at ₹X per claim.\" — and the form warns before you submit a breach. \"Save draft\" keeps a claim private to you (receipts attach to saved drafts); \"Submit\" files it. If the category list cannot be loaded, filing is blocked with an error — the form never guesses at a default.",
        ui_refs: ["expense-input-type", "expense-cap-note", "expense-add-line", "expense-form-save-draft", "expense-form-submit"],
      },
      {
        id: "expenses-routing",
        roles: ALL,
        heading: "Where your claim goes",
        body:
          "Two signatures by different people, decided by who files: a Sales Officer's expense goes to their Sales Manager, then the Admin Manager; a Sales Manager's to the Admin Manager, then the Director; the Admin Manager's to the Director alone. A Sales Officer's expense ABOVE the escalation threshold skips the Sales Manager and goes to the Admin Manager, then the Director — still exactly two signatures, and the form tells you before you submit: \"This exceeds the ₹X threshold…\". The routing is stamped when you submit; a later change to the threshold never re-routes a claim already in a queue. Money is released only by the final signature — the status then reads \"Approved for payment release\".",
        ui_refs: ["expense-escalation-hint", "expense-claim-total"],
      },
      {
        id: "expenses-track",
        roles: ALL,
        heading: "Tracking and deciding",
        body:
          "\"My expenses\" shows each claim with its live status — a claim between stages reads \"With Sales Manager\" (or whichever role holds it). Managers file their own claims from the same page via the Me/Team toggle. Approvers act from the claim detail or the Approvals queue; a stage-2 rejection sends the claim back to stage 1 as rework with the reason attached, and the filer amends and the loop continues — it is never silently capped. Tech Admin and Director see the \"Company Expense Log\" with monthly totals.",
        ui_refs: ["my-expenses-submit-all", "expense-approve-button", "expense-detail-timeline"],
      },
    ],
  },
  {
    nav: "/leaves",
    title: "Leave Management",
    blocks: [
      {
        id: "leaves-model",
        roles: ALL,
        heading: "Two leave types: CL and PL",
        body:
          "The company has two leave types. Casual Leave (CL) is granted annually and lapses at year end; sickness files under CL. Privilege Leave (PL) accrues from working days on duty, carries forward up to its ceiling, and is availed only in spells longer than the minimum the policy sets — the form tells you the number. Leave is charged in WORKING days: Sundays and declared holidays inside your span cost nothing, and the form shows the arithmetic before you submit — \"Requesting N working days of CL … Balance: N\". A span with no working days is refused. If your balance cannot cover the span, the form warns: the shortfall \"will be marked as Loss of Pay (LOP) upon approval\".",
        ui_refs: ["apply-leave-type", "apply-days-summary", "apply-no-working-days", "apply-lop-warning"],
      },
      {
        id: "leaves-lifecycle",
        roles: ALL,
        heading: "From request to decision — and back",
        body:
          "Submit with a reason; your request takes the same two-signature path as an expense. Stage 1 verifies — your balance is untouched. Only the FINAL approval spends it. You can cancel a pending or an approved leave with \"Cancel leave\"; cancelling an approved leave refunds the balance exactly. While a leave is approved for a day, the app blocks check-ins and DCRs dated inside it, by name — a leave day is a leave day.",
        ui_refs: ["apply-leave-submit", "leave-cancel-button", "leave-detail-timeline"],
      },
      {
        id: "leaves-balances",
        roles: APPROVERS,
        heading: "Team balances and adjustments",
        body:
          "The \"Leave balances\" panel on the team view shows each person's balance / accrued per type, PL lapsed, and LOP for the current year. Tech Admin and Director additionally hold \"Company Leave Admin\": every leave, plus the audited balance-adjust dialog — every manual adjustment takes a reason and is logged. PL opening balances are entered there, one per person, when the Director sets them.",
        ui_refs: ["leave-balances-panel", "leave-balances-toggle", "all-leaves-adjust-button"],
      },
    ],
  },
  {
    nav: "/field-map",
    title: "Field Map",
    blocks: [
      {
        id: "field-map-pins",
        roles: APPROVERS,
        heading: "Where the team is today",
        body:
          "One question, answered honestly: who has checked in today, and where. Every pin is a check-in captured TODAY — someone who has not checked in gets no pin, even if an older location exists; that older position appears on their card, dated, never on the map. The heading counts \"people in scope\", and each card claims only what was recorded: \"Checked in\" with times, \"Yet to check in today\", \"On leave\", or — for anything the server sent that the app does not recognise — \"Status not reported\", with the raw value quoted and nothing inferred from it.",
        ui_refs: ["field-map-heading", "field-map-pin-note", "field-map-status-", "field-map-stale-"],
      },
      {
        id: "field-map-capture",
        roles: APPROVERS,
        heading: "When capture is off",
        body:
          "Location is captured once, at check-in — this is not a live GPS feed. If location capture is switched off (it stays off until the consent notice is approved and published), the page says so in a banner served by the server, and no pins are claimed. A person's position line reads \"Not captured.\" with the server's reason when one was reported.",
        ui_refs: ["field-map-capture-off", "field-map-position-"],
      },
    ],
  },
  {
    nav: "/analytics",
    title: "Analytics",
    blocks: [
      {
        id: "analytics-mine",
        roles: ALL,
        heading: "Your performance snapshot",
        body:
          "\"My performance\" shows your last 30 days of visits, this month's expenses, your CL and PL balances (accrued · availed · lapsed), and your attendance month by status. When a figure fails to load the tile says so — \"Leave balance did not load. This is not a balance of zero.\" — because a failed fetch and an empty record are different facts.",
        ui_refs: ["analytics-scope-me", "my-analytics-visits-chart", "leave-balance-unavailable", "attendance-unavailable"],
      },
      {
        id: "analytics-team",
        roles: APPROVERS,
        heading: "Team and company analytics",
        body:
          "The scope toggle switches between your view and the wider one — \"Team\" for managers, \"Company\" for Tech Admin and Director. Charts cover visits per day (last 30 days), expenses by type this month, approved leave days this year, and the DCR submission rate — defined on the chart itself: the share of people in scope logging at least one visit, averaged over working days with Sundays and holidays excluded.",
        ui_refs: ["analytics-scope-toggle", "analytics-visits-trend-chart", "analytics-dcr-rate-chart"],
      },
    ],
  },
  {
    nav: "/samples",
    title: "Sample Log",
    blocks: [
      {
        id: "samples-log",
        roles: ALL,
        heading: "Logging a sample issuance",
        body:
          "\"Log sample\" records what you handed out: doctor, product, quantity, issued date, and the compliance fields — \"Batch no.\" (from the pack) and \"Expiry\". Samples given during a visit can also be added on the DCR itself; those rows show \"From DCR\". The month's chips total your issuance by product. A compliance field that was not captured says so on the row — \"Batch not captured\" — rather than hiding.",
        ui_refs: ["samples-issue-button", "samples-batch-input", "samples-expiry-input", "samples-compliance-"],
      },
      {
        id: "samples-ucpmp",
        roles: ALL,
        heading: "The UCPMP ceilings — advice, never a block",
        body:
          "Before you log, the form shows where the doctor stands against the pack ceiling: \"{doctor} has had {used} of {ceiling} packs of {product} this year\", and projects what your issuance would make it. At or past 80% it warns; over the limit it says plainly: the issuance is STILL RECORDED, and the Admin Manager and the Director are notified — this is advice, not a block. Sample VALUE ceilings depend on product prices and the year's sales basis; until those numbers are entered the panel reads \"Not computable yet\" and names the missing input, rather than showing a wrong figure.",
        ui_refs: ["pack-ceiling-standing", "pack-ceiling-projection", "ucpmp-panel", "ucpmp-pack-clear"],
      },
      {
        id: "samples-admin",
        roles: MASTER_ADMINS,
        heading: "The team ledger",
        body:
          "Managers see team issuance; the Admin Manager, Tech Admin and Director can log on behalf of an officer (only that person's doctors are offered) and edit or delete entries. Sample issuance is a compliance record, so deletion hides and excludes the entry but retains it — \"Show deleted entries\" finds and restores it. \"Top recipients by units issued this month\" flags concentration at a glance.",
        ui_refs: ["samples-admin-dialog", "samples-show-deleted", "samples-top-grid", "samples-delete-confirm"],
      },
    ],
  },
  {
    nav: "/distributors",
    title: "Distributors",
    blocks: [
      {
        id: "distributors-directory",
        roles: ALL,
        heading: "The distributor directory",
        body:
          "Stockists and distributors, mapped to territory units. A Sales Officer sees the distributors in their own territory — the record's territory unit decides which officers can see it. Search by name, contact or city; filter by area. Archived distributors stay in the record and can be restored.",
        ui_refs: ["distributors-search", "distributors-area-filter", "distributors-show-archived"],
      },
      {
        id: "distributors-manage",
        roles: MASTER_ADMINS,
        heading: "Managing the master",
        body:
          "\"Add distributor\" takes name, contact person, mobile (validated like every mobile in the app), the territory unit — the caption says why it matters: \"Decides which Sales Officers see this distributor.\" — area and city. A distributor with no unit is VISIBLE TO NO OFFICER; the banner counts these (\"N distributors visible to no Sales Officer\") and \"Show only these\" filters to them so the gap can be closed record by record. Edits file for approval like every master change.",
        ui_refs: ["distributors-create-button", "distributors-gap-banner", "distributors-gap-toggle", "distributor-save"],
      },
    ],
  },
  {
    nav: "/products",
    title: "Products",
    blocks: [
      {
        id: "products-master",
        roles: MASTER_ADMINS,
        heading: "The medicine master",
        body:
          "Every medicine in the DCR and sample pickers. \"Add Medicine\" takes the name, pack size, HSN code, composition and prices. A blank price shows as \"Not set\" everywhere — it is never treated as zero, and totals that would need it say \"not computable\" instead. The page counts which medicines still need details, and each carries the server's own remark about what is missing. Changes file as master changes and apply at the final signature.",
        ui_refs: ["product-add", "not-set", "product-needs-completion-", "product-form-save"],
      },
      {
        id: "products-prices",
        roles: MASTER_ADMINS,
        heading: "Prices are effective-dated",
        body:
          "Saving a price writes a NEW row from the date you give; the old row keeps valuing the claims filed against it. Nothing is overwritten — \"Prices — {name}\" shows every price ever recorded, newest first. The \"DPCO scheduled\" switch marks price-controlled medicines. Renaming a medicine is a real rename: past visits, sample issuances and doctor lists move to the new name, and the old name is kept in the medicine's history.",
        ui_refs: ["product-prices-", "product-price-history", "product-form-dpco"],
      },
      {
        id: "products-archive",
        roles: MASTER_ADMINS,
        heading: "Archiving, with its consequences counted",
        body:
          "Archiving removes a medicine from the DCR and sample dropdowns but keeps it in every past record. Before you confirm, the dialog counts what references the medicine and tells you; restore is always available. Nothing is deleted, ever.",
        ui_refs: ["product-archive-", "product-archive-references", "product-archive-confirm"],
      },
    ],
  },
  {
    nav: "/territories",
    title: "Territories",
    blocks: [
      {
        id: "territories-master",
        roles: MASTER_ADMINS,
        heading: "The territory master",
        body:
          "The assignable units — Bihar districts plus the four Patna zones — in two sections. Officers and managers can hold several units. One special unit exists outside the assignable set: \"Patna-Head Office\" is the HQ/Office location, and the app refuses to assign it to doctors, distributors, or field staff — head office is where the company sits, not a beat.",
        ui_refs: ["territory-zones", "territory-districts", "territory-add"],
      },
      {
        id: "territories-lifecycle",
        roles: MASTER_ADMINS,
        heading: "Renames and archiving",
        body:
          "\"Rename\" replaces the name on every user currently holding the unit — history keeps the old name, and the toast counts who was updated. \"Archive\" removes the unit from pickers but keeps it in past records — and it is REFUSED while anybody still holds the unit, so nobody's assignment can vanish out from under them. Restore is always available.",
        ui_refs: ["territory-rename-", "territory-archive-", "territory-archive-confirm"],
      },
    ],
  },
  {
    nav: "/team",
    title: "Team",
    blocks: [
      {
        id: "team-roster",
        roles: APPROVERS,
        heading: "The roster",
        body:
          "Everyone on the team: role, contact, territory units, and who they report to. A phone the app cannot dial is flagged — \"not a valid mobile, not dialable\" — and a missing one says \"No phone number on file\". Filters cover role, active status, and \"Show archived users (departed staff)\". An officer with no Sales Manager is flagged in red — \"No Sales Manager — their expenses, leaves and tour plans cannot be verified by anybody. Induct them into a team.\" — because that officer has no stage-1 approver at all.",
        ui_refs: ["team-search-input", "team-phone-invalid-", "team-show-archived", "team-no-manager-"],
      },
      {
        id: "team-changes",
        roles: APPROVERS,
        heading: "Induct, reassign, release — one signature",
        body:
          "Team changes are operational actions, not claims: they take ONE signature, not two. \"Induct member\" places an unassigned Sales Officer into a team; \"Reassign\" moves an officer to another manager with doctors, visits and history intact; \"Release\" hands an officer to another manager — their account, doctors, visits and history stay as they are, and the release NAMES the manager taking them over, because every Sales Officer must always have a Sales Manager to verify their claims at stage 1. A change filed by a Sales Manager or the Admin Manager goes for its one approval (\"Sent for approval — Admin Manager or Director\") — nobody approves what they filed, no role exempt. Tech Admin and Director apply directly: \"Applied — you hold direct authority, no approval step\".",
        ui_refs: ["team-induct-button", "induct-submit", "reassign-submit", "release-submit", "team-pending-change-"],
      },
      {
        id: "team-accounts",
        roles: COMPANY_VIEW,
        heading: "Invites and departures",
        body:
          "\"Invite user\" sends a single-use link — the recipient sets their own password, and the link expires in 7 days. (Email delivery is currently mocked: copy the link from the confirmation screen and share it yourself.) \"Pending invites\" tracks unaccepted ones. Archiving a user treats them as departed: hidden from lists and pickers, sign-in revoked, and every historical record — visits, expenses, leaves, attendance — retained. Restore is available under \"Show archived users\".",
        ui_refs: ["team-invite-button", "invite-submit", "invite-copy-link", "team-tab-invites", "team-archive-confirm"],
      },
    ],
  },
  {
    nav: "/settings",
    title: "Settings",
    blocks: [
      {
        id: "settings-policy",
        roles: COMPANY_VIEW,
        heading: "Policy lives here, not in code",
        body:
          "Settings holds the numbers the company can change without a developer: the leave policy (CL days per year, PL accrual rate, ceiling, warning threshold, and minimum spell), the expense escalation threshold — \"Strictly greater than this amount escalates — a claim of exactly ₹X does not.\" — and the two optional extra price columns on the product master. Changing the escalation threshold affects only claims submitted AFTER the change; nothing already in a queue moves.",
        ui_refs: ["settings-leave-policy-section", "settings-escalation-threshold", "settings-price-fields-section"],
      },
      {
        id: "settings-lists",
        roles: COMPANY_VIEW,
        heading: "The company's lists",
        body:
          "The holiday master feeds attendance, leave arithmetic and tour planning — a declared holiday costs no leave and takes no plan. Expense categories are the list Sales Officers file under; removing one keeps existing records intact — \"Existing records that reference this value won't be deleted, but new entries won't see it.\" Every list here is served to the forms from this single source, so what a form offers is always what approval accepts. The company profile (name, address, logo) rounds it out.",
        ui_refs: ["settings-holidays-section", "settings-expense-types-section", "settings-company-section"],
      },
    ],
  },
  {
    nav: "/user-guide",
    title: "User Guide",
    blocks: [
      {
        id: "guide-about",
        roles: ALL,
        heading: "About this guide",
        body:
          "This guide describes the app as built, and shows you only the sections your role can reach — what you read here is what your sidebar offers. Button and field names are quoted exactly as they appear on screen, and the build verifies each one against the running code, so the guide cannot silently drift from the app. If something on screen disagrees with this guide, the screen is newer: report it to the Tech Admin.",
        ui_refs: [],
      },
    ],
  },
];
